import type { TaskUpdateBody } from "@api/task.api";
import { isEntityLockedTaskType, type Task } from "@common/types/task";
import { TaskColumn } from "@common/types/views/tasksView";
import { taskViewConfig } from "../taskViewConfig";

/** A task field a card edits through a chip or a shortcut. */
export type TaskCardEditField =
    | "status"
    | "assignee"
    | "priority"
    | "dueDate"
    | "entities"
    | "group"
    | "estimate"
    | "stage"
    | "service";

/** A List column's name, as the Board gear and a card's +N list say it too. */
export const getTaskColumnName = (column: TaskColumn) =>
    taskViewConfig.columns[column].name;

/** Each card field's name: List's column name, as the Board gear says it too. */
export const TASK_CARD_FIELD_LABELS: Record<TaskCardEditField, string> = {
    status: getTaskColumnName(TaskColumn.STATUS),
    assignee: getTaskColumnName(TaskColumn.ASSIGNEE),
    priority: getTaskColumnName(TaskColumn.PRIORITY),
    dueDate: getTaskColumnName(TaskColumn.DUE_DATE),
    entities: getTaskColumnName(TaskColumn.ENTITIES),
    group: getTaskColumnName(TaskColumn.GROUP),
    estimate: getTaskColumnName(TaskColumn.ESTIMATE),
    stage: getTaskColumnName(TaskColumn.CLOSE_STAGE),
    service: getTaskColumnName(TaskColumn.CLOSE),
};

/** A single-card edit: what to send and, for an assignee, who the cache shows until the response lands. */
export interface TaskCardEdit {
    task: Task;
    body: TaskUpdateBody;
    assignee?: Task["assignee"];
    /** A toast confirming the edit, for a field the card doesn't show. */
    confirmation?: string;
}

/** What the cache shows for an edit until the response lands: its body, plus the picked assignee. */
export const getTaskCardEditPatch = ({
    body,
    assignee,
}: Pick<TaskCardEdit, "body" | "assignee">) =>
    assignee === undefined ? body : { ...body, assignee };

/** Whether a card can edit `field`; an entity-locked task type keeps its entities, as in List. */
export const isTaskFieldEditable = ({
    task,
    field,
}: {
    task: Pick<Task, "type">;
    field: TaskCardEditField;
}) => field !== "entities" || !isEntityLockedTaskType(task.type);
